import React, { useEffect, useMemo, useState } from 'react';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import type { SelectChangeEvent } from '@mui/material/Select';
import {
  Alert,
  Box,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined';
import OilBarrelOutlinedIcon from '@mui/icons-material/OilBarrelOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined';
import {
  GhgScope,
  ScopeEmissionsSummary,
  UtilityActivityLine,
  UtilityActivityType,
  UtilityCategory,
} from '../../types/utility-emission.type';
import { listUtilityCatalog } from '../../services/utility-emission.service';

enum UtilityBreakdownSortField {
  UTILITY = 'utility',
  ACTIVITY = 'activity',
  SCOPE = 'scope',
  FACTOR_VERSION = 'factor_version',
  EMISSIONS = 'emissions',
}

enum SortDirection {
  ASC = 'asc',
  DESC = 'desc',
}

enum ScopeFilter {
  ALL = 'all',
  SCOPE_1 = 'scope_1',
  SCOPE_2 = 'scope_2',
  NOT_SCOPED = 'not_scoped',
}

const formatTco2e = (value: number | null) =>
  value === null
    ? 'Incomplete'
    : `${value.toLocaleString('en-US', { maximumFractionDigits: 4 })} tCO2e`;

const utilityIcons: Record<
  UtilityCategory,
  React.ComponentType<SvgIconProps>
> = {
  [UtilityCategory.ELECTRICITY]: BoltOutlinedIcon,
  [UtilityCategory.GAS]: LocalFireDepartmentOutlinedIcon,
  [UtilityCategory.OIL]: OilBarrelOutlinedIcon,
  [UtilityCategory.WATER]: WaterDropOutlinedIcon,
  [UtilityCategory.OTHER]: ScienceOutlinedIcon,
};

interface ScopeEmissionsPanelProps {
  summary: ScopeEmissionsSummary;
  showBreakdown?: boolean;
  showScope1?: boolean;
  showScope2?: boolean;
}

const ScopeCard: React.FC<{
  label: string;
  value: number | null;
}> = ({ label, value }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      minHeight: 88,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 600, mt: 0.5 }}>
      {formatTco2e(value)}
    </Typography>
  </Paper>
);

function compareLines(
  left: UtilityActivityLine,
  right: UtilityActivityLine,
  field: UtilityBreakdownSortField
): number {
  switch (field) {
    case UtilityBreakdownSortField.ACTIVITY:
      return left.consumption - right.consumption;
    case UtilityBreakdownSortField.SCOPE:
      return (left.ghg_scope ?? Number.MAX_SAFE_INTEGER) -
        (right.ghg_scope ?? Number.MAX_SAFE_INTEGER);
    case UtilityBreakdownSortField.FACTOR_VERSION:
      return (left.factor_version_label ?? '').localeCompare(
        right.factor_version_label ?? ''
      );
    case UtilityBreakdownSortField.EMISSIONS:
      return (left.emissions_tco2e ?? Number.MAX_SAFE_INTEGER) -
        (right.emissions_tco2e ?? Number.MAX_SAFE_INTEGER);
    case UtilityBreakdownSortField.UTILITY:
    default:
      return left.label.localeCompare(right.label);
  }
}

function matchesScope(line: UtilityActivityLine, filter: ScopeFilter): boolean {
  switch (filter) {
    case ScopeFilter.SCOPE_1:
      return line.ghg_scope === GhgScope.SCOPE_1;
    case ScopeFilter.SCOPE_2:
      return line.ghg_scope === GhgScope.SCOPE_2;
    case ScopeFilter.NOT_SCOPED:
      return line.ghg_scope === null;
    case ScopeFilter.ALL:
    default:
      return true;
  }
}

const UtilityBreakdownTable: React.FC<{
  lines: UtilityActivityLine[];
}> = ({ lines }) => {
  const [catalog, setCatalog] = useState<UtilityActivityType[]>([]);
  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>(ScopeFilter.ALL);
  const [sortField, setSortField] = useState<UtilityBreakdownSortField>(
    UtilityBreakdownSortField.UTILITY
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    SortDirection.ASC
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    let active = true;
    void listUtilityCatalog()
      .then((utilityTypes) => {
        if (active) setCatalog(utilityTypes);
      })
      .catch(() => {
        if (active) setCatalog([]);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    setPage(0);
  }, [lines]);

  const completeLines = useMemo(() => {
    const existingKeys = new Set(lines.map((line) => line.utility_type_key));
    const notApplicableLines: UtilityActivityLine[] = catalog
      .filter(
        (utilityType) =>
          utilityType.ghg_scope === null && !existingKeys.has(utilityType.key)
      )
      .map((utilityType) => ({
        utility_type_key: utilityType.key,
        label: utilityType.label,
        category: utilityType.category,
        canonical_unit: utilityType.canonical_unit,
        ghg_scope: null,
        consumption: 0,
        amount_sar: 0,
        factor_version_label: null,
        emissions_tco2e: 0,
        missing_factor: false,
      }));
    return [...lines, ...notApplicableLines];
  }, [catalog, lines]);

  const filteredLines = useMemo(() => {
    const query = search.trim().toLowerCase();
    return completeLines
      .filter((line) => {
        if (!matchesScope(line, scopeFilter)) return false;
        if (!query) return true;
        return [
          line.label,
          line.utility_type_key,
          line.category,
          line.canonical_unit,
          line.factor_version_label ?? '',
        ].some((value) => value.toLowerCase().includes(query));
      })
      .sort((left, right) => {
        const result = compareLines(left, right, sortField);
        return sortDirection === SortDirection.ASC ? result : -result;
      });
  }, [completeLines, scopeFilter, search, sortDirection, sortField]);

  const visibleLines = filteredLines.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleSort = (field: UtilityBreakdownSortField) => {
    if (sortField === field) {
      setSortDirection((current) =>
        current === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC
      );
    } else {
      setSortField(field);
      setSortDirection(SortDirection.ASC);
    }
    setPage(0);
  };

  const handleScopeFilter = (event: SelectChangeEvent<ScopeFilter>) => {
    setScopeFilter(event.target.value as ScopeFilter);
    setPage(0);
  };

  const sortableHeader = (
    label: string,
    field: UtilityBreakdownSortField,
    align: 'left' | 'right' = 'left'
  ) => (
    <TableCell align={align} sortDirection={sortField === field ? sortDirection : false}>
      <TableSortLabel
        active={sortField === field}
        direction={sortField === field ? sortDirection : SortDirection.ASC}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          alignItems: 'center',
        }}
      >
        <TextField
          size="small"
          label="Search utilities"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(0);
          }}
          sx={{ flex: '1 1 240px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="scope-filter-label">Scope</InputLabel>
          <Select
            labelId="scope-filter-label"
            value={scopeFilter}
            label="Scope"
            onChange={handleScopeFilter}
          >
            <MenuItem value={ScopeFilter.ALL}>All scopes</MenuItem>
            <MenuItem value={ScopeFilter.SCOPE_1}>Scope 1</MenuItem>
            <MenuItem value={ScopeFilter.SCOPE_2}>Scope 2</MenuItem>
            <MenuItem value={ScopeFilter.NOT_SCOPED}>Not applicable</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow>
              {sortableHeader('Utility', UtilityBreakdownSortField.UTILITY)}
              {sortableHeader('Activity', UtilityBreakdownSortField.ACTIVITY)}
              {sortableHeader('Scope', UtilityBreakdownSortField.SCOPE)}
              {sortableHeader(
                'Factor version',
                UtilityBreakdownSortField.FACTOR_VERSION
              )}
              {sortableHeader(
                'Emissions',
                UtilityBreakdownSortField.EMISSIONS,
                'right'
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleLines.map((line) => {
              const Icon = utilityIcons[line.category];
              return (
                <TableRow hover key={line.utility_type_key}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Icon color="primary" fontSize="small" />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {line.label}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {line.consumption.toLocaleString()} {line.canonical_unit}
                  </TableCell>
                  <TableCell>
                    {line.ghg_scope ? `Scope ${line.ghg_scope}` : 'Not applicable'}
                  </TableCell>
                  <TableCell>
                    {line.factor_version_label ?? (
                      <Chip
                        size="small"
                        color={line.missing_factor ? 'warning' : 'default'}
                        label="Not applicable"
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {formatTco2e(line.emissions_tco2e)}
                  </TableCell>
                </TableRow>
              );
            })}
            {visibleLines.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">
                    No utilities match the current filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      <TablePagination
        component="div"
        count={filteredLines.length}
        page={page}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(Number(event.target.value));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25]}
      />
    </Paper>
  );
};

const ScopeEmissionsPanel: React.FC<ScopeEmissionsPanelProps> = ({
  summary,
  showBreakdown = true,
  showScope1 = true,
  showScope2 = true,
}) => {
  const showCards = showScope1 || showScope2;
  if (!showCards && !showBreakdown) return null;

  return (
    <Box>
      {summary.missing_factor_utility_keys.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Calculation is incomplete for: {summary.missing_factor_utility_keys.join(', ')}.
        </Alert>
      )}
      {showCards && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: showScope1 && showScope2 ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)',
            },
            gap: 2,
            mb: showBreakdown ? 2 : 3,
          }}
        >
          {showScope1 && <ScopeCard label="Scope 1" value={summary.scope_1_tco2e} />}
          {showScope2 && <ScopeCard label="Scope 2" value={summary.scope_2_tco2e} />}
        </Box>
      )}
      {showBreakdown && <UtilityBreakdownTable lines={summary.activity_lines} />}
    </Box>
  );
};

export default ScopeEmissionsPanel;
